import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import iconSave from "../../assets/Profile/IconSave.png";
import { useAuth } from "../../context/AuthContext";
import { updateEnterpriseProfile } from "../../context/Auth";
import { CustomInput } from "../../components/CustomInput";
import Swal from "sweetalert2";

const Field = ({ label, children }) => (
    <div className="flex flex-col gap-1">
        <p className="text-[12px] text-gray-600">{label}</p>
        {children}
    </div>
);

const SectionTitle = ({ title, subtitle }) => (
    <div className="mt-6 mb-3">
        <p className="text-[14px] font-semibold text-gray-700">{title}</p>
        {subtitle && <p className="text-[12px] text-gray-500 mt-0.5">{subtitle}</p>}
        <hr className="border-t border-nemu-plata mt-2" />
    </div>
);

const EnterpriseInfo = ({ enterpriseData, targetUserId }) => {
    const [formHasChanges, setFormHasChanges] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { enterprise, updateProfileEnterprise, user } = useAuth();

    const ent = enterpriseData ?? enterprise.enterprise;

    const defaultVals = (src) => ({
        name: src.name || "",
        giro: src.giro || "",
        city: src.city || "",
        address: src.address || "",
        website: src.website || "",
        delivered_projects: src.delivered_projects ?? "",
        enterprise_description: src.enterprise_description || "",
        rights_transfer: src.rights_transfer || false,
        cesion_porcentaje: src.cesion_porcentaje != null ? parseFloat(src.cesion_porcentaje) : "",
        contact_name: src.contact_name || "",
        contact_position: src.contact_position || "",
        contact_phone: src.contact_phone || "",
        contact_email: src.contact_email || "",
        inventory_type: src.inventory_type || "",
        inventory_link: src.inventory_link || "",
        material_url: src.material_url || "",
        link_user: src.link_user || "",
        link_password: src.link_password || "",
        whatsapp_group: src.whatsapp_group || "",
        extras: src.extras || "",
        comentarios: src.comentarios || "",
    });

    const { register, handleSubmit, formState: { errors, isDirty }, watch, reset } = useForm({
        defaultValues: defaultVals(ent),
    });

    const giro = watch("giro");
    const rightsTransfer = watch("rights_transfer");
    const inventoryType = watch("inventory_type");

    useEffect(() => {
        setFormHasChanges(isDirty);
    }, [isDirty]);

    useEffect(() => {
        if (enterpriseData) reset(defaultVals(enterpriseData));
    }, [enterpriseData]);

    const onSubmit = async (data) => {
        try {
            if (targetUserId) {
                await updateEnterpriseProfile(targetUserId, data);
            } else {
                await updateProfileEnterprise(user.id, data);
            }
            reset(data);
            setFormHasChanges(false);
            Swal.fire({ icon: "success", title: "Éxito", text: "Información actualizada correctamente" });
        } catch (error) {
            Swal.fire({ icon: "error", title: "Error", text: "Hubo un problema al actualizar la información" });
        }
    };

    const inputClass = "w-full px-3 py-2 border border-nemu-plata rounded bg-transparent text-sm focus:outline-none focus:ring focus:ring-nemu-azul";

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-1 mb-4">
                {Object.keys(errors).length > 0 && (
                    <div className="flex flex-col gap-1 w-full px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded">
                        {errors.name && <span className="text-red-600 text-sm">{errors.name.message}</span>}
                    </div>
                )}
                <span className="font-medium">En esta sección llenarás los datos generales de tu empresa.</span>
            </div>

            {/* ── Información de la empresa ── */}
            <SectionTitle title="Información de la empresa" />
            <div className="grid grid-cols-2 gap-6">
                <Field label="Giro">
                    <select className={inputClass} {...register("giro")}>
                        <option value="">Selecciona un giro</option>
                        <option value="Inmobiliaria">Inmobiliaria</option>
                        <option value="Desarrolladora">Desarrolladora</option>
                    </select>
                </Field>
                <Field label="Nombre de la empresa">
                    <CustomInput
                        bg="bg-transparent"
                        type="text"
                        placeholder="Desarrollos Inmobiliarios Mex, S.A. de C.V."
                        border="border-nemu-plata"
                        {...register("name", { required: "El nombre es obligatorio" })}
                    />
                </Field>
                <Field label="Ciudad">
                    <CustomInput
                        bg="bg-transparent"
                        type="text"
                        placeholder="Guadalajara"
                        border="border-nemu-plata"
                        {...register("city")}
                    />
                </Field>
                <Field label="Dirección">
                    <CustomInput
                        bg="bg-transparent"
                        type="text"
                        placeholder="Av. Chapultepec 123, Col. Americana"
                        border="border-nemu-plata"
                        {...register("address")}
                    />
                </Field>
                <Field label="Sitio web">
                    <CustomInput
                        bg="bg-transparent"
                        type="url"
                        placeholder="https://www.empresa.com"
                        border="border-nemu-plata"
                        {...register("website")}
                    />
                </Field>
                {giro === "Desarrolladora" && (
                    <Field label="No. de proyectos entregados">
                        <CustomInput
                            bg="bg-transparent"
                            type="number"
                            placeholder="0"
                            border="border-nemu-plata"
                            {...register("delivered_projects")}
                        />
                    </Field>
                )}
                <div className="col-span-2">
                    <Field label={
                        giro === "Inmobiliaria" ? "Descripción de Inmobiliaria"
                        : giro === "Desarrolladora" ? "Descripción de Desarrolladora"
                        : "Descripción"
                    }>
                        <textarea
                            className={`${inputClass} resize-none`}
                            rows={3}
                            maxLength={500}
                            placeholder="Describe brevemente la empresa..."
                            {...register("enterprise_description")}
                        />
                    </Field>
                </div>
                {giro === "Desarrolladora" && (
                    <>
                        <Field label="Cesión de derechos">
                            <div className="flex items-center gap-3 mt-1">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 accent-nemu-azul cursor-pointer"
                                    {...register("rights_transfer")}
                                />
                                <span className="text-sm text-gray-600">Aplica cesión de derechos</span>
                            </div>
                        </Field>
                        {rightsTransfer && (
                            <Field label="Porcentaje de cesión de derechos">
                                <CustomInput
                                    bg="bg-transparent"
                                    type="number"
                                    placeholder="0"
                                    border="border-nemu-plata"
                                    {...register("cesion_porcentaje")}
                                />
                            </Field>
                        )}
                    </>
                )}
            </div>

            {/* ── Contacto Principal ── */}
            <SectionTitle
                title="Contacto Principal"
                subtitle="Este será el contacto visible para usuarios de Nemu que deseen realizar alianzas o solicitar información."
            />
            <div className="grid grid-cols-2 gap-6">
                <Field label="Nombre del contacto principal">
                    <CustomInput
                        bg="bg-transparent"
                        type="text"
                        placeholder="Juan Pérez"
                        border="border-nemu-plata"
                        {...register("contact_name")}
                    />
                </Field>
                <Field label="Cargo">
                    <select className={inputClass} {...register("contact_position")}>
                        <option value="">Selecciona un cargo</option>
                        <option value="Director">Director</option>
                        <option value="Gerente">Gerente</option>
                        <option value="Encargado de alianzas">Encargado de alianzas</option>
                        <option value="Asesor">Asesor</option>
                        <option value="Otro">Otro</option>
                    </select>
                </Field>
                <Field label="Teléfono">
                    <CustomInput
                        bg="bg-transparent"
                        type="text"
                        placeholder="+52 (33) 1234-5678"
                        border="border-nemu-plata"
                        {...register("contact_phone")}
                    />
                </Field>
                <Field label="Correo">
                    <CustomInput
                        bg="bg-transparent"
                        type="email"
                        placeholder="contacto@empresa.com"
                        border="border-nemu-plata"
                        autoComplete="off"
                        {...register("contact_email")}
                    />
                </Field>
            </div>

            {/* ── Inventario ── */}
            <SectionTitle title="Inventario" />
            <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                    <Field label="Tipo de inventario">
                        <select className={inputClass} {...register("inventory_type")}>
                            <option value="">Selecciona una opción</option>
                            <option value="Drive/Link/App">Drive / Link / App</option>
                            <option value="Contacto Principal">Contacto Principal</option>
                            <option value="Grupo de WhatsApp">Grupo de WhatsApp</option>
                            <option value="Otro">Otro</option>
                        </select>
                    </Field>
                </div>
                {inventoryType === "Drive/Link/App" && (
                    <>
                        <Field label="Link de inventario">
                            <CustomInput
                                bg="bg-transparent"
                                type="url"
                                placeholder="https://..."
                                border="border-nemu-plata"
                                {...register("inventory_link")}
                            />
                        </Field>
                        <Field label="Link de materiales">
                            <CustomInput
                                bg="bg-transparent"
                                type="url"
                                placeholder="https://..."
                                border="border-nemu-plata"
                                {...register("material_url")}
                            />
                        </Field>
                        <Field label="Usuario">
                            <CustomInput
                                bg="bg-transparent"
                                type="text"
                                placeholder="usuario"
                                border="border-nemu-plata"
                                autoComplete="off"
                                {...register("link_user")}
                            />
                        </Field>
                        <Field label="Contraseña">
                            <div className="flex items-center">
                                <CustomInput
                                    bg="bg-transparent"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="contraseña"
                                    border="border-nemu-plata"
                                    autoComplete="new-password"
                                    {...register("link_password")}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="ml-2 text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap"
                                >
                                    {showPassword ? "Ocultar" : "Ver"}
                                </button>
                            </div>
                        </Field>
                    </>
                )}
                {inventoryType === "Grupo de WhatsApp" && (
                    <div className="col-span-2">
                        <Field label="Link o nombre de grupo de WhatsApp">
                            <CustomInput
                                bg="bg-transparent"
                                type="text"
                                placeholder="https://chat.whatsapp.com/... o nombre del grupo"
                                border="border-nemu-plata"
                                {...register("whatsapp_group")}
                            />
                        </Field>
                    </div>
                )}
            </div>

            {/* ── Adicional ── */}
            <SectionTitle title="Adicional" />
            <div className="grid grid-cols-2 gap-6">
                <Field label="Recursos adicionales">
                    <textarea
                        className={`${inputClass} resize-none`}
                        rows={3}
                        maxLength={500}
                        placeholder="Drive, carpeta comercial, grupo de WhatsApp, sitio web, materiales promocionales, etc."
                        {...register("extras")}
                    />
                </Field>
                <Field label="Notas internas">
                    <textarea
                        className={`${inputClass} resize-none`}
                        rows={3}
                        maxLength={500}
                        placeholder="Notas internas de soporte"
                        {...register("comentarios")}
                    />
                </Field>
            </div>

            <hr className="border-t border-nemu-plata mt-6 mb-4" />
            <button
                type="submit"
                className={`flex w-max h-[100%] gap-[8px] justify-center rounded-[4px] px-[16px] py-[8px] ${
                    formHasChanges ? "bg-nemu-azul cursor-pointer" : "bg-nemu-plata"
                }`}
                disabled={!formHasChanges}
            >
                <img src={iconSave} alt="Save Icon" />
                <p className="text-white">Guardar cambios</p>
            </button>
        </form>
    );
}

export { EnterpriseInfo };
